import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { MessageStoreState, ChatMessage, Conversation, MessageStatus, MessageCursor, isGroupConversation, getGroupConversationId } from '../types/message';
import { SocialEvent } from '../types/websocket';

export const useMessageStore = create<MessageStoreState>()(
  devtools(
    (set, get) => ({
      messages: new Map(),
      conversations: new Map(),
      unreadCount: new Map(),
      pendingRequestCount: 0,
      socialEvents: [] as SocialEvent[],
      currentConversation: null,
      totalUnreadCount: 0,
      watermarks: new Map(),
      messageCursors: new Map(),
      messageIdSet: new Map(),
      unreadVersions: new Map(),

      addMessage: (conversationId: string, message: ChatMessage) => {
        set((state) => {
          const messages = state.messages.get(conversationId) || [];
          const updatedMessages = [...messages, message];
          const updatedMessageIdSet = new Map(state.messageIdSet);
          const idSet = updatedMessageIdSet.get(conversationId) || new Set<string>();
          idSet.add(message.id);
          updatedMessageIdSet.set(conversationId, idSet);
          
          const updatedConversations = new Map(state.conversations);
          const conversation = updatedConversations.get(conversationId);
          
          const messageTimestamp = message.timestamp ? Number(message.timestamp) : Date.now();
          
          if (conversation) {
            updatedConversations.set(conversationId, {
              ...conversation,
              lastMessage: message,
              lastMessageAt: messageTimestamp,
            });
          } else {
            const isGroup = isGroupConversation(conversationId);
            let groupId: number | undefined;

            if (isGroup && message.receiverId) {
              const parsed = Number(message.receiverId);
              if (!isNaN(parsed) && parsed > 0) {
                groupId = parsed;
              }
            }

            updatedConversations.set(conversationId, {
              id: conversationId,
              type: isGroup ? 'group' : 'friend',
              name: isGroup ? `Group ${groupId || ''}` : message.senderId,
              avatar: undefined,
              groupId,
              lastMessage: message,
              lastMessageAt: messageTimestamp,
              unreadCount: 0
            });
          }
          
          return {
            messages: new Map(state.messages).set(conversationId, updatedMessages),
            conversations: updatedConversations,
            messageIdSet: updatedMessageIdSet,
          };
        });
      },

      updateMessageStatus: (conversationId: string, messageId: string, status: MessageStatus) => {
        set((state) => {
          const messages = state.messages.get(conversationId) || [];
          const updatedMessages = messages.map(msg =>
            msg.id === messageId ? { ...msg, status } : msg
          );
          
          return {
            messages: new Map(state.messages).set(conversationId, updatedMessages)
          };
        });
      },

      updateMessage: (conversationId: string, messageId: string, updates: Partial<ChatMessage>) => {
        set((state) => {
          const messages = state.messages.get(conversationId) || [];
          const updatedMessages = messages.map(msg =>
            msg.id === messageId ? { ...msg, ...updates } : msg
          );
          const updatedMessageIdSet = new Map(state.messageIdSet);
          const idSet = updatedMessageIdSet.get(conversationId);
          if (idSet && updates.id && updates.id !== messageId) {
            idSet.delete(messageId);
            idSet.add(updates.id);
          }
          
          return {
            messages: new Map(state.messages).set(conversationId, updatedMessages),
            messageIdSet: updatedMessageIdSet
          };
        });
      },

      getMessages: (conversationId: string) => {
        return get().messages.get(conversationId) || [];
      },

      setMessages: (conversationId: string, messages: ChatMessage[]) => {
        set((state) => {
          const updatedMessageIdSet = new Map(state.messageIdSet);
          const idSet = new Set<string>(messages.map(m => m.id));
          updatedMessageIdSet.set(conversationId, idSet);
          return {
            messages: new Map(state.messages).set(conversationId, messages),
            messageIdSet: updatedMessageIdSet
          };
        });
      },

      addConversation: (conversation: Conversation) => {
        set((state) => {
          const existingConversation = state.conversations.get(conversation.id);
          if (existingConversation) {
            const merged = {
              ...existingConversation,
              ...conversation,
              unreadCount: existingConversation.unreadCount,
              lastMessageAt: conversation.lastMessageAt ?? existingConversation.lastMessageAt,
              groupName: conversation.groupName ?? existingConversation.groupName,
              groupAvatarPath: conversation.groupAvatarPath ?? existingConversation.groupAvatarPath,
              name: conversation.name ?? existingConversation.name,
              avatar: conversation.avatar ?? existingConversation.avatar,
            };
            return {
              conversations: new Map(state.conversations).set(conversation.id, merged)
            };
          }
          return {
            conversations: new Map(state.conversations).set(conversation.id, conversation)
          };
        });
      },

      updateConversation: (conversationId: string, updates: Partial<Conversation>) => {
        set((state) => {
          const conversation = state.conversations.get(conversationId);
          if (!conversation) return state;
          
          return {
            conversations: new Map(state.conversations).set(
              conversationId,
              { ...conversation, ...updates }
            )
          };
        });
      },

      getConversation: (conversationId: string) => {
        return get().conversations.get(conversationId);
      },

      setCurrentConversation: (conversationId: string | null) => {
        set({ currentConversation: conversationId });
      },

      getCurrentConversation: () => {
        return get().currentConversation;
      },

      markAsRead: (conversationId: string) => {
        set((state) => {
          const updatedConversations = new Map(state.conversations);
          const conversation = updatedConversations.get(conversationId);
          if (conversation) {
            updatedConversations.set(conversationId, {
              ...conversation,
              unreadCount: 0
            });
          }
          
          const updatedUnreadCount = new Map(state.unreadCount).set(conversationId, 0);
          let totalUnread = 0;
          updatedUnreadCount.forEach((count) => {
            totalUnread += count;
          });
          
          return {
            conversations: updatedConversations,
            unreadCount: updatedUnreadCount,
            totalUnreadCount: totalUnread + state.pendingRequestCount
          };
        });
      },

      incrementUnreadCount: (conversationId: string) => {
        set((state) => ({
          unreadCount: new Map(state.unreadCount).set(
            conversationId,
            (state.unreadCount.get(conversationId) || 0) + 1
          )
        }));
      },

      getUnreadCount: (conversationId: string) => {
        return get().unreadCount.get(conversationId) || 0;
      },

      syncUnreadCount: (conversationId: string, count: number) => {
        set((state) => {
          const updatedConversations = new Map(state.conversations);
          const conversation = updatedConversations.get(conversationId);
          if (conversation) {
            updatedConversations.set(conversationId, {
              ...conversation,
              unreadCount: count
            });
          }
          
          const updatedUnreadCount = new Map(state.unreadCount).set(conversationId, count);
          let totalUnread = 0;
          updatedUnreadCount.forEach((c) => {
            totalUnread += c;
          });
          
          return {
            conversations: updatedConversations,
            unreadCount: updatedUnreadCount,
            totalUnreadCount: totalUnread + state.pendingRequestCount
          };
        });
      },

      syncAllUnreadCounts: (counts: Record<string, number>) => {
        set((state) => {
          const updatedConversations = new Map(state.conversations);
          const updatedUnreadCount = new Map(state.unreadCount);
          
          Object.entries(counts).forEach(([conversationId, count]) => {
            updatedUnreadCount.set(conversationId, count);
            
            const conversation = updatedConversations.get(conversationId);
            if (conversation) {
              updatedConversations.set(conversationId, {
                ...conversation,
                unreadCount: count
              });
            }
          });
          
          let totalUnread = 0;
          updatedUnreadCount.forEach((count) => {
            totalUnread += count;
          });
          
          return {
            conversations: updatedConversations,
            unreadCount: updatedUnreadCount,
            totalUnreadCount: totalUnread + state.pendingRequestCount
          };
        });
      },

      clearMessages: (conversationId: string) => {
        set((state) => ({
          messages: new Map(state.messages).set(conversationId, [])
        }));
      },

      setPendingRequestCount: (count: number) => {
        set({ pendingRequestCount: count });
      },

      incrementPendingRequestCount: () => {
        set((state) => ({
          pendingRequestCount: state.pendingRequestCount + 1
        }));
      },

      decrementPendingRequestCount: () => {
        set((state) => ({
          pendingRequestCount: Math.max(0, state.pendingRequestCount - 1)
        }));
      },

      getPendingRequestCount: () => {
        return get().pendingRequestCount;
      },

      addSocialEvent: (event: SocialEvent) => {
        set((state) => ({
          socialEvents: [event, ...state.socialEvents].slice(0, 100)
        }));
        const et = event.eventType;
        const isFriendRequest = et === 9;
        const isGroupJoinRequest = et === 12;
        const isGroupInvite = et === 15;
        if (isFriendRequest || isGroupJoinRequest || isGroupInvite) {
          get().incrementPendingRequestCount();
        }
      },

      clearSocialEvents: () => {
        set({ socialEvents: [] });
      },

      getTotalUnreadCount: () => {
        const state = get();
        let totalUnread = 0;
        state.unreadCount.forEach((count) => {
          totalUnread += count;
        });
        return totalUnread + state.pendingRequestCount;
      },

      batchSetConversations: (conversations: Map<string, Conversation>) => {
        set((state) => {
          const updatedConversations = new Map(state.conversations);
          conversations.forEach((conv, id) => {
            const existing = updatedConversations.get(id);
            if (existing) {
              updatedConversations.set(id, {
                ...existing,
                ...conv,
                unreadCount: conv.unreadCount ?? existing.unreadCount,
                lastMessageAt: conv.lastMessageAt ?? existing.lastMessageAt,
                groupName: conv.groupName ?? existing.groupName,
                groupAvatarPath: conv.groupAvatarPath ?? existing.groupAvatarPath,
                name: conv.name ?? existing.name,
                avatar: conv.avatar ?? existing.avatar,
              });
            } else {
              updatedConversations.set(id, conv);
            }
          });
          return { conversations: updatedConversations };
        });
      },

      batchSetMessages: (messagesMap: Map<string, ChatMessage[]>) => {
        set((state) => {
          const updatedMessages = new Map(state.messages);
          messagesMap.forEach((msgs, convId) => {
            updatedMessages.set(convId, msgs);
          });
          const updatedMessageIdSet = new Map(state.messageIdSet);
          messagesMap.forEach((msgs, convId) => {
            updatedMessageIdSet.set(convId, new Set<string>(msgs.map(m => m.id)));
          });
          return { messages: updatedMessages, messageIdSet: updatedMessageIdSet };
        });
      },

      setWatermark: (conversationId: string, timestamp: number) => {
        set((state) => ({
          watermarks: new Map(state.watermarks).set(conversationId, timestamp)
        }));
      },

      getWatermark: (conversationId: string) => {
        return get().watermarks.get(conversationId) || 0;
      },

      resolveConversationsWithGroupInfo: (groups: any[]) => {
        set((state) => {
          const updatedConversations = new Map(state.conversations);
          let changed = false;

          for (const group of groups) {
            const groupId = Number(group.groupId || group.id || 0);
            if (!groupId) continue;

            const conversationId = getGroupConversationId(String(groupId));
            const conversation = updatedConversations.get(conversationId);
            if (!conversation) continue;

            const groupName = group.groupName || group.name;
            const groupAvatarPath = group.avatarPath || group.avatar;

            const needsNameUpdate = !conversation.groupName &&
              (!conversation.name || conversation.name === conversationId || isGroupConversation(conversation.name));
            const needsAvatarUpdate = !conversation.groupAvatarPath && !conversation.avatar;
            const needsUpdate = needsNameUpdate || needsAvatarUpdate;

            if (needsUpdate) {
              updatedConversations.set(conversationId, {
                ...conversation,
                groupName: groupName || conversation.groupName,
                groupAvatarPath: groupAvatarPath || conversation.groupAvatarPath,
                name: groupName || conversation.name,
                avatar: groupAvatarPath || conversation.avatar,
              });
              changed = true;
            }
          }

          return changed ? { conversations: updatedConversations } : state;
        });
      },

      addMessageId: (conversationId: string, messageId: string) => {
        set((state) => {
          const updatedMessageIdSet = new Map(state.messageIdSet);
          const idSet = updatedMessageIdSet.get(conversationId) || new Set<string>();
          idSet.add(messageId);
          updatedMessageIdSet.set(conversationId, idSet);
          return { messageIdSet: updatedMessageIdSet };
        });
      },

      hasMessageId: (conversationId: string, messageId: string) => {
        const idSet = get().messageIdSet.get(conversationId);
        return idSet ? idSet.has(messageId) : false;
      },

      setMessageCursor: (conversationId: string, cursor: MessageCursor) => {
        set((state) => ({
          messageCursors: new Map(state.messageCursors).set(conversationId, cursor)
        }));
      },

      getMessageCursor: (conversationId: string) => {
        return get().messageCursors.get(conversationId);
      },

      syncUnreadCountWithVersion: (conversationId: string, count: number, version: number) => {
        set((state) => {
          const currentVersion = state.unreadVersions.get(conversationId) || 0;
          if (version <= currentVersion) return state;

          const updatedConversations = new Map(state.conversations);
          const conversation = updatedConversations.get(conversationId);
          if (conversation) {
            updatedConversations.set(conversationId, {
              ...conversation,
              unreadCount: count
            });
          }

          const updatedUnreadCount = new Map(state.unreadCount).set(conversationId, count);
          const updatedUnreadVersions = new Map(state.unreadVersions).set(conversationId, version);
          let totalUnread = 0;
          updatedUnreadCount.forEach((c) => {
            totalUnread += c;
          });

          return {
            conversations: updatedConversations,
            unreadCount: updatedUnreadCount,
            unreadVersions: updatedUnreadVersions,
            totalUnreadCount: totalUnread + state.pendingRequestCount
          };
        });
      }
    }),
    {
      name: 'message-store'
    }
  )
);
